import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useAuthContext } from './Auth';
import { useAppContext } from './App';
import axios from 'axios';
import { Demand, Farm, FarmSection, Field, GraphDemandData, Variety } from './types';
import { calculateCentroid, sortTableColumn } from 'Helpers/helpers';
import DashboardAppDeps from 'di/DashboardAppDeps';
import HttpClient from 'http/HttpClient';
import useFarmsQuery from 'Helpers/useFarmsQuery';
import moment from 'moment';
import farms from '../../cypress/fixtures/register-organization/farms.json';

type PDFReportContextType = {
  x_api_key: string;
  setXApiKey: React.Dispatch<React.SetStateAction<string>>;
  getFarms: (x_api_key: string) => void;
  fetchFieldsData: (x_api_key: string) => void;
  fetchFarmSections: (farm: Farm, x_api_key: string) => void;
  getVarietyForecastData: (
    x_api_key: string,
    variety_id: number,
    start_date: string,
    end_date: string,
    farm_section_id: number | null
  ) => Promise<any | null>;
  getVarietyHarvestData: (
    x_api_key: string,
    variety_id: number,
    start_date: string,
    end_date: string,
    farm_section_id: number | null
  ) => Promise<any | null>;
  getVarietyMilestoneForecastData: (
    x_api_key: string,
    variety_id: number,
    start_date: string,
    end_date: string
  ) => Promise<any | null>;
  getDemand: (
    variety_id: number,
    start_date: string,
    end_date: string
  ) => Promise<GraphDemandData[] | null>;
  loadingForecasts: boolean;
  loadingFields: boolean;
  error: string | null;
  fields: Field[];
  weatherData: any;
  loadingWeather: boolean;
  fetchWeatherData: (
    num_of_future_days: number,
    past_start_day: string | undefined,
    past_end_day: string | undefined
  ) => void;
  farmSections: FarmSection[];
};

const MyContext = createContext<PDFReportContextType>({
  x_api_key: '',
  setXApiKey: () => {},
  getFarms: (x_api_key: string) => {},
  fetchFieldsData: (x_api_key: string) => {},
  fetchFarmSections: (farm: Farm, x_api_key: string) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVarietyForecastData: async (
    x_api_key: string,
    variety_id: number,
    start_date: string,
    end_date: string,
    farm_section_id: number | null
  ) => {
    return Promise.resolve();
  },
  getVarietyHarvestData: async (
    x_api_key: string,
    variety_id: number,
    start_date: string,
    end_date: string,
    farm_section_id: number | null
  ) => {
    return Promise.resolve();
  },
  getVarietyMilestoneForecastData: async (
    x_api_key: string,
    variety_id: number,
    start_date: string,
    end_date: string
  ) => {
    return Promise.resolve();
  },
  getDemand: async (variety_id: number, start_date: string, end_date: string) => {
    return Promise.resolve([]);
  },
  loadingFields: true,
  loadingForecasts: false,
  error: null,
  fields: [],
  weatherData: null,
  loadingWeather: false,
  fetchWeatherData: async (
    num_of_future_days: number,
    past_start_day: string | undefined,
    past_end_day: string | undefined
  ) => {},
  farmSections: []
});

export const BASE_URL = `${process.env.REACT_APP_BE_URL}`;

export const usePDFReportContext = () => useContext(MyContext);
const appDeps: DashboardAppDeps = DashboardAppDeps.getInstance();
const httpClient: HttpClient = appDeps.provideHttpClient();

export default function PDFReportContext({ children }: { children: ReactNode }): JSX.Element {
  const {
    functions: { getToken },
    variables: { user }
  } = useAuthContext();
  const { farm, setFarm, farms, setFarms, setVarieties } = useAppContext();

  const [loadingForecasts, setLoadingForecasts] = useState<boolean>(false);
  const [loadingFields, setLoadingFields] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [fields, setFields] = useState<Field[]>([]);
  const [farmSections, setFarmSections] = useState<FarmSection[]>([]);

  const [loadingWeather, setLoadingWeather] = useState<boolean>(false);
  const [weatherData, setWeatherData] = useState<any>(false);

  const [x_api_key, setXApiKey] = useState<string>('');
  const farmsQuery = useFarmsQuery({
    currentUserEmail: user?.email,
    token: getToken(),
    x_api_key: x_api_key,
    forceInvalidateQuery: false
  });

  useEffect(() => {
    setFarms(farmsQuery.data ?? []);
    let sorted_farms = [];
    if (farmsQuery?.data?.length) sorted_farms = sortTableColumn(farmsQuery.data, 'name', 'asc');
    setFarms(sorted_farms);
    if (farm == null) {
      setFarm(sorted_farms[0]);
      if (sorted_farms[0]) {
        fetchVarieties(sorted_farms[0], x_api_key);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmsQuery.data]);

  const getFarms = (x_api_key: string): void => {
    farmsQuery.refetch();
  };

  const fetchVarieties = async (farm: Farm, x_api_key: string) => {
    const response = await httpClient
      .getAxios()
      .get(`${BASE_URL}/customers/${farm?.customer?.id}/varieties`, {
        headers: {
          ...(x_api_key ? { 'x-api-key': x_api_key } : { Authorization: getToken() })
        },
        params: { customer_id: farm?.customer?.id }
      })
      .catch(() => {});
    if (response && response.data) {
      const filtered_varieties = response.data.filter(
        (el: any) => el.farm_id == farm?.id
      ) as Variety[];
      if (farm && farm.id == 'all') {
        setFarm({ ...farm, varieties: filtered_varieties });
        setVarieties(filtered_varieties);
      }
    }
  };
  const fetchFarmSections = async (farm: Farm, x_api_key: string) => {
    const response = await httpClient
      .getAxios()
      .get(`${BASE_URL}/farm-sections/${farm?.id}/sections`, {
        headers: {
          ...(x_api_key ? { 'x-api-key': x_api_key } : { Authorization: getToken() })
        },
        params: { farm_id: farm?.id }
      })
      .catch(() => {});
    if (response && response.data) {
      setFarmSections(response.data as FarmSection[]);
    }
  };

  const fetchFieldsData = async (x_api_key: string) => {
    if (!loadingFields) {
      setLoadingFields(true);
      setFields([]);
      const response = await httpClient
        .getAxios()
        .get(`${BASE_URL}/farms/${farm?.id}/fields`, {
          headers: {
            ...(x_api_key ? { 'x-api-key': x_api_key } : { Authorization: getToken() })
          },
          params: { include_area_value: true, customer_id: farm?.customer?.id }
        })
        .catch((err) => {
          setLoadingFields(false);
          setError(err?.response?.data?.detail);
        });
      // check if response is of type AxiosResponse<any, any>
      if (response && response?.data) {
        setFields([...(response?.data as Field[])]);
      }
      setLoadingFields(false);
    }
  };

  async function getVarietyForecastData(
    x_api_key: string,
    variety_id: number,
    start_date: string,
    end_date: string,
    farm_section_id: number | null
  ): Promise<any | null> {
    setLoadingForecasts(true);
    const res = await httpClient
      .getAxios()
      .get(`${BASE_URL}/varieties/${variety_id}/latest-forecasts`, {
        headers: {
          ...(x_api_key ? { 'x-api-key': x_api_key } : { Authorization: getToken() })
        },
        params: {
          start_date: start_date,
          end_date: end_date,
          granularity: 'weeks',
          all_farms: false,
          customer_id: farm?.customer?.id,
          farm_section_id: farm_section_id
        }
      })
      .catch(() => {});
    let forecastResponse;
    if (res && res?.status === 403) forecastResponse = null;
    else if (res?.data) {
      forecastResponse = res.data;
    }
    setLoadingForecasts(false);
    return forecastResponse;
  }
  async function getVarietyHarvestData(
    x_api_key: string,
    variety_id: number,
    start_date: string,
    end_date: string,
    farm_section_id: number | null
  ): Promise<any | null> {
    setLoadingForecasts(true);
    const res = await httpClient
      .getAxios()
      .get(`${BASE_URL}/varieties/${variety_id}/graph-harvests`, {
        headers: {
          ...(x_api_key ? { 'x-api-key': x_api_key } : { Authorization: getToken() })
        },
        params: {
          start_date: start_date,
          end_date: end_date,
          granularity: 'weeks',
          all_farms: false,
          customer_id: farm?.customer?.id
        }
      })
      .catch(() => {});
    let forecastResponse;
    if (res && res?.status === 403) forecastResponse = null;
    else if (res?.data) {
      forecastResponse = res.data;
    }
    setLoadingForecasts(false);
    return forecastResponse;
  }
  async function getVarietyMilestoneForecastData(
    x_api_key: string,
    variety_id: number,
    start_date: string,
    end_date: string
  ): Promise<any | null> {
    setLoadingForecasts(true);

    // get milestones 2weeks out and 1week out
    const res = await httpClient
      .getAxios()
      .get(`${BASE_URL}/varieties/${variety_id}/milestone-forecasts`, {
        headers: {
          ...(x_api_key ? { 'x-api-key': x_api_key } : { Authorization: getToken() })
        },
        params: {
          start_date: start_date,
          end_date: end_date,
          granularity: 'weeks',
          all_farms: false,
          milestones: ['weeks_out_1', 'weeks_out_2'],
          customer_id: farm?.customer?.id
        }
      })
      .catch(() => {});
    let milestoneForecastResponse;
    if (res && res?.status === 403) milestoneForecastResponse = null;
    else if (res?.data) {
      milestoneForecastResponse = res.data;
    }
    setLoadingForecasts(false);
    return milestoneForecastResponse;
  }
  const convertDemandToDetailedView = (demand: Demand): { [key: string]: number } => {
    return {
      [demand.production_category_id]: demand.entry_value ?? 0
    };
  };
  const convertMultipleDemandToDetailedView = (demands: Demand[]): { [key: string]: number } => {
    const detailedView: { [key: string]: number } = {};
    for (let i = 0; i < demands.length; i++) {
      const demand = demands[i];
      const dv = convertDemandToDetailedView(demand);
      Object.keys(dv).forEach((key) => {
        if (detailedView[key]) {
          detailedView[key] += dv[key];
        } else {
          detailedView[key] = dv[key];
        }
      });
    }
    return detailedView;
  };
  async function getDemand(
    variety_id: number,
    start_date: string,
    end_date: string
  ): Promise<GraphDemandData[] | null> {
    setLoadingForecasts(true);
    let demandItems: Demand[] = [];
    const BASE_URL = `${process.env.REACT_APP_BE_URL}`;
    const res = await httpClient
      .getAxios()
      .get(`${BASE_URL}/varieties/${variety_id}/demands`, {
        headers: {
          Authorization: getToken()
        },
        params: {
          start_date: start_date,
          end_date: end_date,
          customer_id: farm?.customer?.id
        }
      })
      .catch(() => {});
    if (res?.data && res.data.length) {
      demandItems = res.data;
    }

    setLoadingForecasts(false);
    const year = Math.max(moment(start_date).year(), moment(end_date).year());
    const isoWeeksInYear = moment().year(year).isoWeeksInYear();
    const sortedDemandData: GraphDemandData[] = [];
    for (let i = 1; i <= isoWeeksInYear; i++) {
      const weekData = demandItems.filter((demand) => moment(demand.entry_date).isoWeek() === i);
      const variety_id = weekData.length ? weekData[0].variety_id : 0;
      let totalYieldValue = 0;
      for (let j = 0; j < weekData.length; j++) {
        const demand = weekData[j];
        totalYieldValue += demand?.entry_value ?? 0;
      }
      const dd: GraphDemandData = {
        _id: '',
        created_at: '',
        updated_at: '',
        variety: variety_id,
        farm_id: '',
        stems: totalYieldValue,
        week: i,
        year: year.toString(),
        grading: convertMultipleDemandToDetailedView(weekData)
      };
      sortedDemandData.push(dd);
    }
    return sortedDemandData;
  }

  const fetchWeatherData = async (
    num_of_future_days: number,
    past_start_day: string | undefined,
    past_end_day: string | undefined
  ) => {
    if (!loadingWeather && farm != null && farm?.location != undefined) {
      setLoadingWeather(true);
      setWeatherData(false);
      const farm_center_coordinate = calculateCentroid(farm.location.polygon);
      let future_response: any;
      if (num_of_future_days > 0) {
        future_response = await axios
          .get(
            `https://api.worldweatheronline.com/premium/v1/weather.ashx?key=${process.env.REACT_APP_WWO_API_KEY}&q=${farm_center_coordinate.lat},${farm?.location?.polygon[0].long}&format=json&num_of_days=${num_of_future_days}&cc=no&includelocation=yes&tp=24`
          )
          .catch((err) => {
            setLoadingWeather(false);
            setError(err?.response?.data?.detail);
          });
        // check if response is of type AxiosResponse<any, any>
        if (future_response && future_response?.data.data && past_start_day == undefined) {
          setWeatherData({ ...future_response?.data.data });
        }
      }

      if (past_start_day) {
        const historical_response = await axios
          .get(
            `https://api.worldweatheronline.com/premium/v1/past-weather.ashx?key=${process.env.REACT_APP_WWO_API_KEY}&q=${farm_center_coordinate.lat},${farm?.location?.polygon[0].long}&format=json&date=${past_start_day}&enddate=${past_end_day}&includelocation=yes&tp=24`
          )
          .catch((err) => {
            setLoadingWeather(false);
            setError(err?.response?.data?.detail);
          });
        // check if response is of type AxiosResponse<any, any>
        if (historical_response && historical_response?.data.data) {
          // merge historical and future weather data
          if (num_of_future_days > 0) {
            const combined_weather_data = historical_response?.data.data?.weather.concat(
              future_response?.data.data.weather
            );
            historical_response.data.data.weather = combined_weather_data;
          }

          setWeatherData({ ...historical_response.data.data });
        }
      }

      setLoadingWeather(false);
    }
  };

  return (
    <MyContext.Provider
      value={{
        x_api_key,
        setXApiKey,
        getFarms,
        fetchFieldsData,
        getVarietyForecastData,
        getVarietyHarvestData,
        getVarietyMilestoneForecastData,
        getDemand,
        loadingFields,
        loadingForecasts,
        fields,
        error,
        weatherData,
        loadingWeather,
        fetchWeatherData,
        fetchFarmSections,
        farmSections
      }}
    >
      {children}
    </MyContext.Provider>
  );
}
